import type { ActiveFilter, FilterDefinition } from '@oysterjs/ui/search-filter';
import type { SortDirection as TableSortDirection } from '@oysterjs/ui/data-table';

import {
  ClientDocumentSortField,
  SortDirection,
  type ClientArtifactFilterInput,
  type ClientDocumentSortInput,
  type ClientFileFilterInput
} from '~/api/graphql';
import { getSinceDate } from '~/api/data/filter-base';
import {
  DocumentDateFilter,
  DocumentSearchFilter,
  DocumentUserFilter,
  type DocumentFilter
} from '~/api/data/documents/documents-filters';

export type ClientDocumentSortKey = 'name' | 'date';

export const createClientArtifactFilterDefinitions = (): FilterDefinition<DocumentFilter>[] => [
  new DocumentUserFilter(),
  new DocumentDateFilter(),
  new DocumentSearchFilter()
];

/** An upload records no author worth filtering on, so its tab offers no user filter. */
export const createClientFileFilterDefinitions = (): FilterDefinition<DocumentFilter>[] => [
  new DocumentDateFilter(),
  new DocumentSearchFilter()
];

/**
 * The server takes one search term and one user, so several search chips are
 * joined into one term and the newest user chip wins. Undefined for no
 * filters, which keeps the unfiltered variables identical to the loader's.
 */
export const activeFiltersToClientArtifactFilter = (
  filters: ActiveFilter<DocumentFilter>[]
): ClientArtifactFilterInput | undefined => {
  if (filters.length === 0) return undefined;
  const input: ClientArtifactFilterInput = {};
  const terms: string[] = [];
  for (const filter of filters) {
    const value = String(filter.value);
    switch (filter.type) {
      case 'search':
        if (value.trim()) terms.push(value.trim());
        break;
      case 'user':
        input.agencyUserId = value;
        break;
      case 'date': {
        const since = getSinceDate(value);
        if (since) input.createdAfter = since;
        break;
      }
    }
  }
  if (terms.length > 0) input.search = terms.join(' ');
  return input;
};

export const activeFiltersToClientFileFilter = (
  filters: ActiveFilter<DocumentFilter>[]
): ClientFileFilterInput | undefined => {
  const artifactFilter = activeFiltersToClientArtifactFilter(filters);
  if (!artifactFilter) return undefined;
  return { search: artifactFilter.search, createdAfter: artifactFilter.createdAfter };
};

export const clientDocumentSortInput = (
  sortBy: ClientDocumentSortKey,
  direction: TableSortDirection
): ClientDocumentSortInput => ({
  field: sortBy === 'name' ? ClientDocumentSortField.Name : ClientDocumentSortField.CreatedAt,
  direction: direction === 'asc' ? SortDirection.Asc : SortDirection.Desc
});
